import { OAuthExtension } from '@magic-ext/oauth2';
import { Magic } from 'magic-sdk';
import { defaultLogger } from '../utils/logger';

const createMagic = () => {
  const apiKey = process.env.REACT_APP_MAGIC_PUBLISHABLE_KEY;
  
  if (!apiKey) {
    throw new Error('REACT_APP_MAGIC_PUBLISHABLE_KEY is not defined');
  }

  // Validate API key format
  if (!apiKey.startsWith('pk_')) {
    defaultLogger.warn('Magic API key does not start with "pk_" - this may indicate an invalid key');
  }

  defaultLogger.info('Initializing Magic', { 
    keyPrefix: apiKey?.substring(0, 10) + '...',
    keyLength: apiKey?.length,
    environment: process.env.REACT_APP_NODE_ENV || 'development'
  });

  const chainId = Number(process.env.REACT_APP_PUBLIC_CHAIN_ID_DEC || 84532);
  const rpcUrl = process.env.REACT_APP_RPC_URL || 'https://sepolia.base.org';
  
  // Log configuration for debugging
  defaultLogger.info('Magic configuration', {
    chainId,
    rpcUrl,
    testMode: false
  });
  
  return new Magic(apiKey, {
    extensions: [new OAuthExtension()],
    testMode: false, // Set to true only for testing
    network: {
      rpcUrl,
      chainId,
    },
  });
};

export const magic = createMagic();

/**
 * Get Magic provider for ZeroDev integration
 */
export function getMagicProvider() {
  try {
    const provider = magic.rpcProvider;
    if (!provider) {
      throw new Error('Failed to get Magic provider');
    }
    return provider;
  } catch (error) {
    defaultLogger.error('Error getting Magic provider', error);
    throw error;
  }
}

/**
 * Validate Magic Link configuration and OAuth setup
 */
export async function validateMagicConfiguration(): Promise<{ isValid: boolean; errors: string[] }> {
  const errors: string[] = [];
  
  try {
    // Check if Magic instance is properly initialized
    if (!magic) {
      errors.push('Magic instance is not initialized');
      return { isValid: false, errors };
    }
    
    // Check API key format
    const apiKey = process.env.REACT_APP_MAGIC_PUBLISHABLE_KEY;
    if (!apiKey) {
      errors.push('REACT_APP_MAGIC_PUBLISHABLE_KEY environment variable is not set');
    } else if (!apiKey.startsWith('pk_')) {
      errors.push('Magic API key should start with "pk_" - current key may be invalid');
    }
    
    // Check if we can access Magic's user methods
    try {
      const isLoggedIn = await magic.user.isLoggedIn();
      defaultLogger.debug('Magic user login status check passed', { isLoggedIn });
    } catch (error) {
      errors.push(`Magic user methods not accessible: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    // // Check OAuth extension
    // try {
    //   // This will throw if OAuth is not properly configured
    //   await magic.oauth2.getRedirectResult({});
    // } catch (error) {
    //   const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    //   if (errorMessage.includes('[-32600]')) {
    //     errors.push('OAuth credentials verification failed - check Magic Link dashboard OAuth configuration');
    //   } else if (errorMessage.includes('RPC Error')) {
    //     errors.push('Magic Link RPC error - check API key and network configuration');
    //   }
    // }
    
    return { isValid: errors.length === 0, errors };
  } catch (error) {
    errors.push(`Configuration validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return { isValid: false, errors };
  }
}